// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import xSetRmaReturnInformation from "@guestsupply/App/Handlers/RequestRma/SetRmaReturnInformation";
import xSetRmaReturnUPSLabel from "@guestsupply/App/Handlers/RequestRma/SetRmaReturnUPSLabel";
import xOrderInformationStyles from "@guestsupply/App/SharedStyles/MyAccountPages/Details/OrderInformationStyles";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import Button from "@guestsupply/MobiusOverrides/Button/Button";
import TextField from "@guestsupply/MobiusOverrides/TextField/TextField";
import { createLinkIconStyles, useValidate } from "@guestsupply/XDL/Utilities";
import breakpointMediaQueries from "@guestsupply/XDL/Utilities/breakpointMediaQueries";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Edit from "@insite/mobius/Icons/Edit";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    session: getSession(state),
    billToState: getCurrentBillToState(state),
    customerInformation: state.xRmaReturnInformation.properties.customerInformation as any,
});

const mapDispatchToProps = {
    loadCurrentBillTo,
    xSetRmaReturnInformation,
};

export interface RequestRmaReturnInformationStyles {
    wrapper?: InjectableCss;
    header?: InjectableCss;
    sectionTitleText?: TypographyProps;
    titleText?: TypographyProps;
    editInfoLink?: LinkPresentationProps;
    addressDisplay?: AddressInfoDisplayStyles;
    divider?: GridItemProps;
    returnInfoGridContainer?: GridContainerProps;
    returnInfoGridItem?: GridItemProps;
    returnInfoFormFieldGridItem?: GridItemProps;
    returnInfoActionsGridItem?: GridItemProps;
    addressFormModal?: ModalPresentationProps;
    returnInfoModalCancelButton?: ButtonPresentationProps;
}
export const requestRmaReturnInformationStyles: RequestRmaReturnInformationStyles = {};
const styles = requestRmaReturnInformationStyles;

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export type rmaReturnCustomerInformationFieldsModel = {
    accountNumber?: string;
    territoryManager?: string;
    invoiceNumber?: string;
    contactName?: string;
    contactPhone?: string;
    contactEmail?: string;
};

export type rmaReturnInformationModel = {
    customerInformation: rmaReturnCustomerInformationFieldsModel;
    UPSReturnLabel: string;
    RMAEmail?: string;
    RMAReplacement: boolean;
};

const RequestRmaReturnInformation: React.FunctionComponent<Props> = ({
    session,
    billToState,
    customerInformation,
    loadCurrentBillTo,
    xSetRmaReturnInformation,
}: Props) => {
    const { value: order } = useContext(OrderStateContext);
    const billTo = billToState.value;
    useEffect(() => {
        if (!billToState.value && !billToState.isLoading) {
            loadCurrentBillTo();
        }
    }, []);

    useEffect(() => {
        if (billToState.value && !billToState.isLoading && order && !isInitialized) {
            xSetRmaReturnInformation({
                customerInformation: {
                    accountNumber: billTo?.customerNumber || "",
                    territoryManager: order?.properties.territoryManager || "",
                    invoiceNumber: order?.properties.invoiceNumber || "",
                    contactName: `${session.firstName} ${session.lastName}` || "",
                    contactPhone: billTo?.phone || "",
                    contactEmail: session.email || "",
                },
            });
            setIsInitialized(true);
        }
    }, [billToState, order]);

    const [editInfoMode, setEditInfoMode] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const { field, touched, errors, handleSubmit, isSubmitting, dirty, values, resetForm, setFieldValue } = useValidate(
        {
            initialValues: {
                accountNumber: billTo?.customerNumber || "",
                territoryManager: order?.properties.territoryManager || "",
                invoiceNumber: order?.properties.invoiceNumber || "",
                contactName: `${session.firstName} ${session.lastName}` || "",
                contactPhone: billTo?.phone || "",
                contactEmail: session.email || "",
            },

            validators: {
                accountNumber: "required",
                territoryManager: "required",
                invoiceNumber: "",
                contactName: "",
                contactPhone: "required",
                contactEmail: "required|max:125|email",
            },
            enableReinitialize: true,
            onSubmit: (fieldValues: rmaReturnCustomerInformationFieldsModel, { setSubmitting }: any) => {
                setSubmitting(true);
                xSetRmaReturnInformation({ customerInformation: fieldValues });
                setSubmitting(false);
                editInfomodalCloseHandler();
            },
        },
    );

    if (!order) {
        return null;
    }

    const editInfomodalCloseHandler = () => {
        setEditInfoMode(false);
    };
    const editInfoModalOpenHandler = () => {
        setEditInfoMode(true);
    };

    const editInfoModalCancelButtonHandler = () => {
        editInfomodalCloseHandler();
        resetForm();
    };

    const StyledForm = getStyledWrapper("form");
    // XNG-Changes
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="rmaRequestReturnInfoWrapper">
            <StyledWrapper {...styles.header}>
                <Typography {...styles.sectionTitleText}>{translate("Customer Return Information")}</Typography>
                <Link
                    {...styles.editInfoLink}
                    onClick={editInfoModalOpenHandler}
                    data-test-selector="Return_Info_Edit_Link"
                    aria-label="Edit Information"
                >
                    {translate("Edit Information")}
                </Link>
            </StyledWrapper>
            <GridItem {...styles.divider} />
            <GridContainer {...styles.returnInfoGridContainer}>
                <>
                    <GridItem {...styles.returnInfoGridItem}>
                        <Typography {...styles.titleText}>{translate("Account #")}</Typography>
                        <Typography>{customerInformation.accountNumber || ""}</Typography>
                    </GridItem>
                    <GridItem {...styles.returnInfoGridItem}>
                        <Typography {...styles.titleText}>{translate("Contact Name")}</Typography>
                        <Typography>{customerInformation.contactName || ""}</Typography>
                    </GridItem>
                    <GridItem {...styles.returnInfoGridItem}>
                        <Typography {...styles.titleText}>{translate("Territory Manager")}</Typography>
                        <Typography>{customerInformation.territoryManager || ""}</Typography>
                    </GridItem>
                    <GridItem {...styles.returnInfoGridItem}>
                        <Typography {...styles.titleText}>{translate("Contact Phone")}</Typography>
                        <Typography>{customerInformation.contactPhone || ""}</Typography>
                    </GridItem>
                    <GridItem {...styles.returnInfoGridItem}>
                        <Typography {...styles.titleText}>{translate("Invoice #")}</Typography>
                        <Typography>{customerInformation.invoiceNumber || ""}</Typography>
                    </GridItem>
                    <GridItem {...styles.returnInfoGridItem}>
                        <Typography {...styles.titleText}>{translate("Contact Email")}</Typography>
                        <Typography>{customerInformation.contactEmail || ""}</Typography>
                    </GridItem>
                </>
                {/* Form Edit Mode Modal */}
                {editInfoMode && (
                    <Modal
                        headline={translate("Edit Customer Return Information")}
                        {...styles.addressFormModal}
                        isOpen={editInfoMode}
                        handleClose={editInfomodalCloseHandler}
                    >
                        <StyledForm onSubmit={handleSubmit} noValidate>
                            <GridContainer>
                                <GridItem {...styles.returnInfoFormFieldGridItem}>
                                    <TextField
                                        type="text"
                                        required
                                        placeholder={translate("Account #")}
                                        label={translate("Account #")}
                                        {...field("accountNumber")}
                                        data-test-selector="rmaForm_AccountNumber"
                                    />
                                </GridItem>

                                <GridItem {...styles.returnInfoFormFieldGridItem}>
                                    <TextField
                                        type="fullName"
                                        placeholder={translate("Contact Name")}
                                        label={translate("Contact Name")}
                                        {...field("contactName")}
                                        data-test-selector="rmaForm_contactName"
                                    />
                                </GridItem>

                                <GridItem {...styles.returnInfoFormFieldGridItem}>
                                    <TextField
                                        type="fullName"
                                        required
                                        placeholder={translate("Territory Manager")}
                                        label={translate("Territory Manager")}
                                        {...field("territoryManager")}
                                        data-test-selector="rmaForm_territoryManager"
                                    />
                                </GridItem>
                                <GridItem {...styles.returnInfoFormFieldGridItem}>
                                    <TextField
                                        type="phone"
                                        required
                                        placeholder={translate("Contact Phone")}
                                        label={translate("Contact Phone")}
                                        {...field("contactPhone")}
                                        data-test-selector="rmaForm_contactPhone"
                                    />
                                </GridItem>
                                <GridItem {...styles.returnInfoFormFieldGridItem}>
                                    <TextField
                                        type="text"
                                        placeholder={translate("Invoice #")}
                                        label={translate("Invoice #")}
                                        {...field("invoiceNumber")}
                                        data-test-selector="rmaForm_invoiceNumber"
                                    />
                                </GridItem>
                                <GridItem {...styles.returnInfoFormFieldGridItem}>
                                    <TextField
                                        type="email"
                                        required
                                        placeholder={translate("Contact Email")}
                                        label={translate("Contact Email")}
                                        {...field("contactEmail")}
                                        data-test-selector="rmaForm_contactEmail"
                                    />
                                </GridItem>
                                <GridItem {...styles.returnInfoActionsGridItem}>
                                    <Button
                                        {...styles.returnInfoModalCancelButton}
                                        data-test-selector="rma_cancelButton"
                                        onClick={editInfoModalCancelButtonHandler}
                                        aria-label="Cancel"
                                    >
                                        {translate("Cancel")}
                                    </Button>
                                    <Button
                                        aria-label="Save"
                                        data-test-selector="rma_saveButton"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {translate("Save")}
                                    </Button>
                                </GridItem>
                            </GridContainer>
                        </StyledForm>
                    </Modal>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestRmaReturnInformation),
    definition: {
        group: "Return Request (RMA)",
        allowedContexts: ["RequestRmaPage"],
    },
};

export default widgetModule;
